import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { replace } from 'connected-react-router'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import usePrevious from 'hooks/usePrevious'
import { useGlobalExperiencesSearchContext, useGlobalExperiencesSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import useExperiencesSearchFilters from '../../Experiences/hooks/useExperiencesSearchFilters'
import useExperiencesSortOptions from '../../Experiences/hooks/useExperiencesSortOptions'
import { usePlaceAncestorsById, usePlaceById } from 'hooks/usePlace'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import useExperiences from 'hooks/Experiences/useExperiences'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GeoContext from 'contexts/geoContext'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import Places, { getPlaceByRegionCode } from 'constants/places'
import { setSearchParamValue, deleteSearchParams } from 'lib/url/searchUrlUtils'
import { without, fillArray } from 'lib/array/arrayUtils'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Error404Page from 'components/Pages/Error404Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LayoutContainer from 'components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import OfferListPagination from 'components/OfferList/OfferListPagination'
import ExperienceSearchMap from './ExperienceSearchMap'
import { SkeletonTile } from '../ExperienceLists/ExperienceSearchList'
import ExperienceSearchTile from '../ExperienceTiles/ExperienceSearchTile'
import { listHasPlaceId } from './utils'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ExperienceSearchHeader from './ExperienceSearchHeader'
import ExperienceSearchPageFiltersSidebar from './ExperienceSearchPageFiltersSidebar'
import useToggle from 'hooks/useToggle'
import Pane from 'components/Common/Pane'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import HeadData from 'components/Common/HeadData'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { getExperiencesSearchHeadMeta } from './getExperiencesSearchHeadMeta'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 100vh;
`

const MapWrapper = styled.div`
  height: 85vh;
  position: relative;
  overflow: hidden;

  ${mediaQueryDown.tablet} {
    height: 100vh;
  }
`

const ToggleMapScreenButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  grid-area: map;
  margin: ${rem(16)};
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
`

const FilterButton = styled(AssistChip)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};
  z-index: 10;
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
  display: none;

  @media (max-width: 1023px) {
    display: inline-flex;
  }
`

const OfferListContainer = styled.div`
  flex: 0 1 50%;
  min-width: 0;
  overflow: auto;
  height: 100%;
  transition: flex-basis .3s ease-in-out, opacity .3s ease-in-out, visibility .3s ease-in-out;

  @media (min-width: 2048px) {
    flex-basis: 40%;
  }
`

const OfferListContent = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  padding-left: ${rem(24)};
  padding-right: ${rem(16)};
  gap: ${rem(16)};
  margin-top: ${rem(16)};
  margin-bottom: ${rem(16)};
`

const MapViewContainer = styled.div`
  flex: 0 1 50%;
  min-width: 0;
  height: 100%;
  transition: flex-basis .3s ease-in-out;

  @media (min-width: 2048px) {
    flex-basis: 60%;
  }
`

const MainContainer = styled(CSSBreakpoint)`
  flex: 1;
  display: flex;
  height: 100%;
  gap: 0;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.default.five};

  &.full-screen-map {
    ${OfferListContainer} {
      flex-basis: 0%;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }

    ${MapViewContainer} {
      flex-basis: 100%;
    }
  }
`

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const Box = styled.div`
  flex: 1;
`

const StyledGroup = styled(Group)`
  padding: ${rem(16)} 0;
`

interface MapPageProps {
  search: string
}

interface Props extends MapPageProps {}

function ExperiencesSearchMapPage({ search }: Props) {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)
  const [page, setPage] = useState<number>(0)
  const [fullScreenMap, setFullScreenMap] = useState(false)
  const [overrideFetching, setOverrideFetching] = useState<boolean>(false)
  const offerListContainerRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const tilesRootRef = useRef<HTMLDivElement>(null)
  const pageSize = 20

  const placeId = queryParams.get('destinationId') ?? undefined
  const {
    searchFilters,
  } = useExperiencesSearchFilters()

  const { globalExperiencesSearchDispatch, globalExperiencesSearchState } = useGlobalExperiencesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    experienceCategories: searchFilters.categoryCodes ?? [],
    experienceCollections: searchFilters.collections ?? [],
  }, queryParams)

  const { sortBy } = useExperiencesSortOptions()

  const [showMobileFilter, toggleShowMobileFilter] = useToggle()

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const regionPlace = useMemo(() => getPlaceByRegionCode(geo.currentRegionCode), [geo.currentRegionCode])

  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(placeId)

  const fallbackPlaceIds = useMemo(() => {
    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...ancestorIds, regionPlace.id, Places.Anywhere.id]
  }, [ancestorPlaceIds, regionPlace])

  const list = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? [] : fallbackPlaceIds,
    categoryCodes: searchFilters.categoryCodes,
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    sortBy,
    bounds: searchFilters.bounds,
    collections: searchFilters.collections ?? [],
  })

  // List without category filters (for category counts)
  const listForCounts = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? [] : fallbackPlaceIds,
    categoryCodes: [], // No category filters for counts
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    sortBy,
    bounds: searchFilters.bounds,
    collections: searchFilters.collections ?? [],
  })

  // Use filtered list when categories are selected, otherwise use the full list for counts
  const totalItems = (searchFilters.categoryCodes && searchFilters.categoryCodes.length > 0)
    ? list.ids.length
    : listForCounts.ids.length

  const experienceIdsLimited = useMemo(() => {
    const startIndex = page * pageSize
    const endIndex = startIndex + pageSize
    return list.ids.slice(startIndex, endIndex)
  }, [list.ids, page, pageSize])

  const [experiences, fetchingExperiences] = useExperiences(experienceIdsLimited)

  // Experiences for category counts (without category filters)
  const experienceIdsForCounts = useMemo(() => {
    const startIndex = 0
    const endIndex = listForCounts.ids.length // Get all experiences for counts
    return listForCounts.ids.slice(startIndex, endIndex)
  }, [listForCounts.ids])

  const [experiencesForCounts] = useExperiences(experienceIdsForCounts)
  const place = usePlaceById(list.placeId)
  const isAnywhereSearch = list.placeId === Places.Anywhere.id
  const metaPlaceName = !isAnywhereSearch ? place?.name : undefined

  const searchHeadMeta = useMemo(() => getExperiencesSearchHeadMeta({
    placeName: metaPlaceName,
    isAnywhere: isAnywhereSearch,
  }), [metaPlaceName, isAnywhereSearch])

  const previousListIds = usePrevious(list.ids)

  useEffect(() => {
    if (previousListIds && previousListIds !== list.ids) {
      setPage(0)
      offerListContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [list.ids, previousListIds])

  const isFetching = list.fetching || fetchingExperiences || overrideFetching

  useGlobalExperiencesSearchResultsTracking(globalExperiencesSearchState, list.ids, !!list.fetching)

  const searchMode = useMemo(() => ({
    url: `/search/experiences?${deleteSearchParams(search, 'sortBy')}`,
    mode: 'map',
  }), [search])

  const resetLoadingState = useMemo(
    () => debounce(() => setOverrideFetching(false), 750),
    [],
  )

  useEffect(() => {
    return () => resetLoadingState.cancel()
  }, [resetLoadingState])

  const handleOfferListLoading = useCallback(() => {
    setOverrideFetching(true)
    resetLoadingState()
  }, [resetLoadingState])

  const handleMapScreenToggle = useCallback(() => {
    setFullScreenMap(!fullScreenMap)
  }, [fullScreenMap])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
    dispatch(replace({ search: setSearchParamValue(search, 'pages', Math.max(newPage, 1)) }))
  }, [dispatch, search])

  const onBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }
    handleOfferListLoading()
    setPage(0)
    const bounds = newBounds.toUrlValue()
    const newSearch = setSearchParamValue(search, 'bounds', bounds)
    dispatch(replace({ search: newSearch }))
  }, [handleOfferListLoading, search, dispatch])

  if (!listHasPlaceId(list)) {
    return <Error404Page />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
    <GlobalSearchContextProvider
      state={globalExperiencesSearchState}
      dispatch={globalExperiencesSearchDispatch}
    >
      <HeadData
        noIndex
        title={searchHeadMeta.title}
        description={searchHeadMeta.description}
        imageId={searchHeadMeta.imageId}
        imageUrl={searchHeadMeta.imageUrl}
        imageAlt={searchHeadMeta.imageAlt}
      />
      <Root ref={rootRef}>
        <Pane shadow="bottom-small">
          <ExperienceSearchPageFiltersSidebar
            open={showMobileFilter}
            place={place}
            onClose={toggleShowMobileFilter}
          />
          <ExperienceSearchHeader
            list={list}
            searchMode={searchMode}
            location={place?.name}
            experiences={experiencesForCounts}
            isFetching={isFetching}
            place={place}
          />
        </Pane>
        <div ref={tilesRootRef} data-experience-search-tiles-root>
          <MapWrapper>
            <MainContainer min="largeDesktop" className={clsx({ 'full-screen-map': fullScreenMap })}>
              <OfferListContainer ref={offerListContainerRef}>
                <OfferListContent>
                  <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                    <div>
                      {!isFetching && <BodyText variant="large">
                        {intl.formatMessage({
                          id: 'r6RhxYF6oF',
                          defaultMessage: '{count, plural, one {# experience} other {# experiences}} found',
                          description: 'Count of experiences found in map view search results',
                        }, { count: totalItems })}
                      </BodyText>}
                      {isFetching && <Loader inline />}
                    </div>
                    <ExperienceSearchPageSort />
                  </Group>
                  {(!experiences || experiences.length === 0 && isFetching) && fillArray(5).map((id) => <SkeletonTile key={id} />)}
                  {!!experiences.length && experiences.map((experience, index) => (
                    <ExperienceSearchTile
                      key={experience.id}
                      position={index}
                      experience={experience}
                      isMapView
                    />
                  ))}
                  {!isFetching && !experiences.length && (
                    <StyledGroup direction="vertical" horizontalAlign="center" verticalAlign="center" gap={8}>
                      <Heading variant="heading3" align="center">
                        {intl.formatMessage({
                          id: 'dYZ3TMpyot',
                          defaultMessage: "Sorry, we couldn't find any experiences that match your search criteria.",
                          description: 'Heading shown when no experiences match filters on map view',
                        })}
                      </Heading>
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'rLWW03NhtS',
                          defaultMessage: 'Try removing some filters or searching a different location to see more options.',
                          description: 'Suggestion text when no experiences match search criteria on map view',
                        })}
                      </BodyText>
                    </StyledGroup>
                  )}
                  <OfferListPagination
                    offerListRef={offerListContainerRef}
                    page={page}
                    total={totalItems}
                    itemsPerPage={pageSize}
                    handlePageChange={handlePageChange}
                  />
                </OfferListContent>
              </OfferListContainer>
              <MapViewContainer>
                <ToggleMapScreenButton
                  kind="primary"
                  variant="ghost"
                  size="large"
                  shape="square"
                  onClick={handleMapScreenToggle}
                >
                  {fullScreenMap ? (
                    <LineAngleRightIcon />
                  ) : (
                    <LineAngleLeftIcon />
                  )}
                </ToggleMapScreenButton>
                <ExperienceSearchMap
                  experiences={experiences}
                  fetching={isFetching}
                  onBoundsChanged={onBoundsChanged}
                  rootRef={tilesRootRef}
                />
              </MapViewContainer>
            </MainContainer>
            <MainContainer max="desktop">
              <Box>
                <FilterButton
                  size="medium"
                  variant="filled"
                  startIcon={<LineFilterAltIcon />}
                  onClick={toggleShowMobileFilter}
                  data-testid="mobile-filter-button"
                >
                  {intl.formatMessage({
                    id: 'NoO7dLfI/Q',
                    defaultMessage: 'Filter',
                    description: 'Mobile filter button text on experience search map page',
                  })}
                </FilterButton>
                <ExperienceSearchMap
                  experiences={experiences}
                  fetching={isFetching}
                  onBoundsChanged={onBoundsChanged}
                  rootRef={tilesRootRef}
                />
              </Box>
            </MainContainer>
          </MapWrapper>
        </div>
      </Root>
    </GlobalSearchContextProvider>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State) => ({
  search: state.router.location.search,
})

export default connect(mapStateToProps)(ExperiencesSearchMapPage)
